import {
  BOOKLIST_QUESTIONS,
  type BooklistQuestionKey,
  type BooklistSheet,
  type ClassCategory,
  type EntrySheet,
  type RequisitionSheetInstitute,
} from '../types/productRequisition.types';

/* ------------------------- specimen requisition sheet ---------------------- */

export interface SheetDraftLine {
  requiredQty: string;
  teacherQty: string;
  studentQty: string;
  targetSaleQty: string;
  targetDate: string;
}

export type SheetDraft = Record<number, SheetDraftLine>;

export const QTY_FIELDS = [
  { key: 'requiredQty', label: 'Requisition Qty' },
  { key: 'teacherQty', label: 'Teacher Qty' },
  { key: 'studentQty', label: 'Student Qty' },
  { key: 'targetSaleQty', label: 'Target Sale Qty' },
] as const;

export const toDraftLine = (row: RequisitionSheetInstitute): SheetDraftLine => ({
  requiredQty: String(row.requiredQty),
  teacherQty: String(row.teacherQty),
  studentQty: String(row.studentQty),
  targetSaleQty: String(row.targetSaleQty),
  targetDate: row.targetDate ?? '',
});

export const toSheetDraft = (institutes: RequisitionSheetInstitute[]): SheetDraft =>
  Object.fromEntries(institutes.map((row) => [row.instituteId, toDraftLine(row)]));

export const requisitionStageLabel = (row: RequisitionSheetInstitute): string => {
  if (row.isApprovedLevelTwo) return 'Approved (2nd)';
  if (row.isApprovedLevelOne) return 'Approved (1st)';
  if (row.isReview) return 'Reviewed';
  if (row.requisitionId === null) return 'Not raised';
  return 'Pending';
};

/* --------------------------- student entry sheet --------------------------- */

// The grid is keyed the way the entry is: institute, class and group together.
// Legacy saved one cell at a time against that same key.
export const cellKey = (instituteId: number, classId: number, classGroup: string): string =>
  `${instituteId}|${classId}|${classGroup}`;

export type EntryDraft = Record<string, string>;

export const toEntryDraft = (sheet: EntrySheet): EntryDraft =>
  Object.fromEntries(
    sheet.institutes.flatMap((institute) =>
      institute.counts.map((count) => [
        cellKey(institute.instituteId, count.classId, count.classGroup),
        String(count.studentQty),
      ]),
    ),
  );

export const classColumnLabel = (category: ClassCategory): string =>
  category.classGroup === 'NA' ? category.name : `${category.name} (${category.classGroup})`;

/* -------------------------- booklisted institution ------------------------- */

export type BooklistAnswers = Record<BooklistQuestionKey, boolean>;

export type BooklistDraft = Record<number, BooklistAnswers>;

export const EMPTY_BOOKLIST_ANSWERS: BooklistAnswers = {
  isSpDistributed: false,
  isBookListed: false,
  isLectureSaleFull: false,
  isLectureSalePartial: false,
  isOtherBookSale: false,
  isNotWorking: false,
};

export const toBooklistDraft = (sheet: BooklistSheet): BooklistDraft =>
  Object.fromEntries(
    sheet.institutes.map((institute): [number, BooklistAnswers] => {
      const given = institute.answers;
      if (given === null) return [institute.instituteId, { ...EMPTY_BOOKLIST_ANSWERS }];

      return [
        institute.instituteId,
        Object.fromEntries(
          BOOKLIST_QUESTIONS.map(({ key }) => [key, given[key]]),
        ) as BooklistAnswers,
      ];
    }),
  );

/* --------------------------------- shared --------------------------------- */

export const toQty = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

export const stageLabel = (row: {
  isReview: boolean;
  isApprovedLevelOne: boolean;
  isApprovedLevelTwo: boolean;
}): string => {
  if (row.isApprovedLevelTwo) return 'Approved (2nd)';
  if (row.isApprovedLevelOne) return 'Approved (1st)';
  if (row.isReview) return 'Reviewed';
  return 'Pending';
};
